import React, { useRef, useEffect, useState, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { sceneConfig } from '../../config/sceneConfig';

const PhoneCable = ({ phoneboxScene, visible = true }) => {
  const cableRef = useRef();
  const geometryRef = useRef(null);
  const phoneAnchorRef = useRef();
  const phoneboxAnchorRef = useRef();
  const lastAnchorPointsRef = useRef({ phone: null, phonebox: null });
  const [cableGeometry, setCableGeometry] = useState(null);
  const [anchorPoints, setAnchorPoints] = useState({ phone: null, phonebox: null });
  const [meshes, setMeshes] = useState({ phone: null, phonebox: null });
  const [isInCorrectPosition, setIsInCorrectPosition] = useState(false);
  const [hasTimedOut, setHasTimedOut] = useState(false);
  
  // Reusable vectors to avoid creating new objects on every frame
  const tempVector1 = useRef(new THREE.Vector3());
  const tempVector2 = useRef(new THREE.Vector3());
  
  // Cable configuration from sceneConfig
  const cableConfig = useMemo(() => ({
    segments: sceneConfig.phoneCable.cable.segments,
    thickness: sceneConfig.phoneCable.cable.thickness,
    color: sceneConfig.phoneCable.cable.color,
    sag: sceneConfig.phoneCable.cable.sag,
    fixedLength: sceneConfig.phoneCable.cable.fixedLength,
    material: sceneConfig.phoneCable.cable.material
  }), []);

  // Find the Phone and phonebox meshes in the scene
  useEffect(() => {
    if (!phoneboxScene) return;

    const findMeshes = () => {
      let phoneMesh = null;
      let phoneboxMesh = null;

      phoneboxScene.traverse((child) => {
        if (child.isMesh) {
          // Direct name matching for renamed meshes
          if (child.name === 'phone') {
            phoneMesh = child;
          } else if (child.name === 'phonebox') {
            phoneboxMesh = child;
          }
        }
      });

      setMeshes({ phone: phoneMesh, phonebox: phoneboxMesh });
    };

    // Add a small delay to ensure the scene is fully loaded
    const timeoutId = setTimeout(findMeshes, 100);
    
    return () => clearTimeout(timeoutId);
  }, [phoneboxScene]);

  // Check if phonebox is in correct position before rendering cable
  useEffect(() => {
    if (!phoneboxScene) {
      setIsInCorrectPosition(false);
      return;
    }

    const currentPosition = phoneboxScene.position;
    
    // Simple check: just make sure we're not at the loading position (0, -65, -150)
    const loadingPosition = new THREE.Vector3(0, -65, -150);
    const distanceFromLoading = currentPosition.distanceTo(loadingPosition);
    const isNotAtLoadingPosition = distanceFromLoading > 2; // Small threshold for loading position
    
    // Allow rendering if we're not at the loading position
    const shouldRender = isNotAtLoadingPosition;
    
    
    setIsInCorrectPosition(shouldRender);
  }, [phoneboxScene]);

  // Fallback timeout: allow cable to render after 2 seconds regardless of position
  useEffect(() => {
    if (!phoneboxScene) return;
    
    const timeoutId = setTimeout(() => {
      setHasTimedOut(true);
    }, 2000);
    
    return () => clearTimeout(timeoutId);
  }, [phoneboxScene]);

  // Setup anchor objects that follow the meshes
  useEffect(() => {
    if (!meshes.phone || !meshes.phonebox) return;

    // Create invisible anchor objects
    const phoneAnchor = new THREE.Object3D();
    const phoneboxAnchor = new THREE.Object3D();

    // Set initial positions relative to meshes from sceneConfig
    phoneAnchor.position.set(...sceneConfig.phoneCable.anchorPoints.phone);
    phoneboxAnchor.position.set(...sceneConfig.phoneCable.anchorPoints.phonebox);

    // Add anchors as children of the meshes
    meshes.phone.add(phoneAnchor);
    meshes.phonebox.add(phoneboxAnchor);

    // Store references
    phoneAnchorRef.current = phoneAnchor;
    phoneboxAnchorRef.current = phoneboxAnchor;

    // Set initial anchor points
    const phoneWorldPos = new THREE.Vector3();
    const phoneboxWorldPos = new THREE.Vector3();
    
    phoneAnchor.getWorldPosition(phoneWorldPos);
    phoneboxAnchor.getWorldPosition(phoneboxWorldPos);

    setAnchorPoints({
      phone: phoneWorldPos,
      phonebox: phoneboxWorldPos
    });


    // Cleanup function
    return () => {
      if (phoneAnchorRef.current && meshes.phone) {
        meshes.phone.remove(phoneAnchorRef.current);
      }
      if (phoneboxAnchorRef.current && meshes.phonebox) {
        meshes.phonebox.remove(phoneboxAnchorRef.current);
      }
      phoneAnchorRef.current = null;
      phoneboxAnchorRef.current = null;
    };
  }, [meshes]);

  // Generate cable geometry using catenary curve with fixed length and variable sag
  useEffect(() => {
    if (!anchorPoints.phone || !anchorPoints.phonebox) {
      return;
    }

    try {
      const start = anchorPoints.phone;
      const end = anchorPoints.phonebox;
      
      // Calculate actual distance between anchor points
      const currentDistance = start.distanceTo(end);
      
      // Only regenerate if distance has changed significantly OR if it's the first time
      const lastDistance = geometryRef.current?.userData?.actualDistance;
      if (lastDistance && Math.abs(currentDistance - lastDistance) < 0.01) {
        return; // Skip regeneration if distance hasn't changed much
      }
        
      // Create catenary curve points that connect both anchor points
      const curvePoints = [];
      const segments = cableConfig.segments;
      
      // Use a reasonable cable length that's long enough to reach both points
      const maxCableLength = cableConfig.fixedLength;
      const cableLength = Math.min(currentDistance * 1.2, maxCableLength); // 20% longer than straight line, capped at max
      
      // Calculate dynamic sag based on the cable length
      const baseSag = cableConfig.sag;
      const sagMultiplier = Math.min(1.0, cableLength / maxCableLength);
      const dynamicSag = baseSag * sagMultiplier;
      
      for (let i = 0; i <= segments; i++) {
        const t = i / segments;
        
        // Linear interpolation between anchor points (ensures proper connection)
        const basePos = tempVector1.current.lerpVectors(start, end, t);
        
        // Calculate catenary sag - maximum at the middle of the cable
        const sagFactor = Math.sin(t * Math.PI); // 0 at ends, 1 at middle
        const sagAmount = sagFactor * dynamicSag;
        
        // Add subtle randomness to make it look more natural
        const noise = Math.sin(t * Math.PI * 3) * 0.005;
        
        const finalPos = tempVector2.current.copy(basePos);
        finalPos.y -= sagAmount + noise;
        
        curvePoints.push(finalPos.clone());
      }

      // Create a tube geometry along the curve
      const geometry = new THREE.TubeGeometry(
        new THREE.CatmullRomCurve3(curvePoints),
        segments,
        cableConfig.thickness,
        8, // radial segments
        false // closed
      );

      // Store actual distance for comparison
      geometry.userData = { actualDistance: currentDistance };

      // Dispose of the previous geometry to prevent memory leaks
      if (geometryRef.current) {
        geometryRef.current.dispose();
      }

      geometryRef.current = geometry;
      setCableGeometry(geometry);
        
    } catch (error) {
      console.error('Error generating cable geometry:', error);
    }

    // Cleanup function to dispose of geometry when component unmounts or dependencies change
    return () => {
      if (geometryRef.current) {
        geometryRef.current.dispose();
        geometryRef.current = null;
      }
    };
  }, [anchorPoints, cableConfig]);


  // Update cable position during animations - optimized to prevent memory leaks
  useFrame((state, delta) => {
    if (!cableRef.current || !phoneAnchorRef.current || !phoneboxAnchorRef.current) return;

    // Force update world matrices to ensure we get the latest positions
    if (meshes.phone) {
      meshes.phone.updateMatrixWorld(true); // Force update
    }
    if (meshes.phonebox) {
      meshes.phonebox.updateMatrixWorld(true); // Force update
    }

    // Get world positions from the anchor objects (they automatically follow their parent meshes)
    phoneAnchorRef.current.getWorldPosition(tempVector1.current);
    phoneboxAnchorRef.current.getWorldPosition(tempVector2.current);
    
    // Use the temp vectors directly but ensure we don't modify them
    const phoneWorldPos = tempVector1.current;
    const phoneboxWorldPos = tempVector2.current;

    // Check if positions have actually changed to avoid unnecessary updates
    const lastPhone = lastAnchorPointsRef.current.phone;
    const lastPhonebox = lastAnchorPointsRef.current.phonebox;
    
    const phoneChanged = !lastPhone || phoneWorldPos.distanceTo(lastPhone) > 0.001; // Reduced threshold for more responsive updates
    const phoneboxChanged = !lastPhonebox || phoneboxWorldPos.distanceTo(lastPhonebox) > 0.001; // Reduced threshold for more responsive updates
    
    // Always update refs to track both positions
    if (phoneChanged || phoneboxChanged) {
      lastAnchorPointsRef.current = {
        phone: phoneWorldPos.clone(),
        phonebox: phoneboxWorldPos.clone()
      };
    }
    
    // Only update cable geometry when the phone moves (phonebox is typically stationary)
    if (phoneChanged) {
      // Update state to trigger geometry regeneration (clone to avoid reference issues)
      setAnchorPoints({
        phone: phoneWorldPos.clone(),
        phonebox: phoneboxWorldPos.clone()
      });
      
    }
  });


  // Cleanup effect for component unmount
  useEffect(() => {
    return () => {
      // Dispose of geometry when component unmounts
      if (geometryRef.current) {
        geometryRef.current.dispose();
        geometryRef.current = null;
      }
    };
  }, []);

  // Don't render if not visible, unless timeout has been reached
  if (!visible || (!isInCorrectPosition && !hasTimedOut)) {
    return null;
  }

  // If we have proper anchor points, render the dynamic cable
  if (cableGeometry && anchorPoints.phone && anchorPoints.phonebox) {
    return (
      <group>
        <mesh ref={cableRef} geometry={cableGeometry} castShadow receiveShadow>
          <meshStandardMaterial
            color={cableConfig.color}
            roughness={cableConfig.material.roughness}
            metalness={cableConfig.material.metalness}
          />
        </mesh>
      </group>
    );
  }

  // No fallback - only render when we have proper anchor points
  return null;
};

export default PhoneCable;
